import React, { useEffect } from 'react'
import { ComponentConfig } from '@puckeditor/core'
import InnerHTML from 'dangerously-set-html-content'
import { withStyleFields } from '../StyleWrapper'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'
import * as styles from './EmbedCode.module.scss'

export type EmbedCodeProps = {
  code: string
  maxWidth?: string
  align?: 'left' | 'center' | 'right'
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

const EmbedCodeRender = ({
  code,
  maxWidth,
  align,
  background,
  border,
}: EmbedCodeProps) => {
  useEffect(() => {
    // Re-trigger Instagram's embed library when an embed mounts after its
    // script already loaded (same pattern as the legacy listicle EmbedCode).
    if (typeof window === 'undefined') return
    const instgrm = (
      window as unknown as {
        instgrm?: { Embeds?: { process?: () => void } }
      }
    ).instgrm
    instgrm?.Embeds?.process?.()
  }, [code])

  const styleProps = applyStyleProps({ background, border })
  const wrapperStyle: React.CSSProperties = {
    maxWidth: maxWidth || '100%',
    marginLeft: align === 'center' || align === 'right' ? 'auto' : undefined,
    marginRight: align === 'center' || align === 'left' ? 'auto' : undefined,
    ...styleProps,
  }

  if (!code) {
    return (
      <div className={styles.placeholder} style={wrapperStyle}>
        Paste embed code (e.g. an &lt;iframe&gt;) to display it here
      </div>
    )
  }

  return (
    <div className={`content-item embed ${styles.wrapper}`} style={wrapperStyle}>
      <InnerHTML key={code} html={code} />
    </div>
  )
}

export const PuckEmbedCode = withStyleFields<ComponentConfig<EmbedCodeProps>>({
  label: 'Embed Code',
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Raw third-party HTML embed (Instagram, TikTok, a form). You cannot set its code: insert the block and ask the person to paste the embed code into it.',
    // The code runs as-is on the page and in the editor: only a person sets it.
    rawCodeFields: ['code'],
  },
  defaultProps: { code: '', maxWidth: '100%', align: 'center' },
  fields: {
    code: {
      type: 'textarea',
      label: 'Embed Code (HTML)',
    },
    maxWidth: { type: 'text', label: 'Max Width (e.g. 450px)' },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
  },
  render: props => <EmbedCodeRender {...props} />,
})
